import React, { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import * as S from './Tabs.styles';
import { Tab, TabsProps } from '@/components/Tabs/Tabs.types';
import { useUrlAndHashFromPath } from '@/hooks/useUrlAndHashFromPath';

export const Tabs: React.FC<TabsProps> = ({
  tabs,
  shape = 'squared',
  onTabCLick,
  hasTopPadding,
  isCompareWithHash = true,
  ...props
}) => {
  const router = useRouter();
  const tabsRef = useRef<HTMLDivElement>(null);
  const { url: urlWithoutHash } = useUrlAndHashFromPath();

  const url = isCompareWithHash ? router.asPath : urlWithoutHash;

  function onClick(tab: Tab) {
    if (tab.link) {
      router.replace(tab.link);
    } else {
      onTabCLick?.(tab);
    }
  }

  function isTabActive(tab: Tab) {
    return url === tab.link || tab.isActive;
  }

  function scrollToActiveItem() {
    if (tabsRef.current) {
      const desiredItemIndex = tabs.findIndex(tab => isTabActive(tab));

      if (desiredItemIndex === -1) return;

      let scrollPosition = 0;

      for (let i = 0; i < desiredItemIndex; i++) {
        const item = tabsRef.current.children[i] as HTMLElement;
        if (item) {
          scrollPosition += item.offsetWidth;
        }
      }

      tabsRef.current.scrollLeft = scrollPosition;
    }
  }

  useEffect(() => {
    scrollToActiveItem();
  }, [url]);

  return (
    <S.Box
      $shape={shape}
      $hasTopPadding={hasTopPadding}
      {...props}
      ref={tabsRef}
    >
      {tabs.map((tab, i) => {
        const isActive = isTabActive(tab);
        const isFirst = i === 0;
        const isLast = i === tabs.length - 1;

        return (
          <S.Tab
            key={tab.label}
            appearance="transparent"
            onClick={() => onClick(tab)}
            isFullWidth={shape === 'squared'}
            $isFirst={isFirst}
            $isLast={isLast}
            $isActive={isActive}
            $shape={shape}
            data-stat={tab.dataStat}
          >
            {tab.label}
          </S.Tab>
        );
      })}
    </S.Box>
  );
};
